// src/Pages/Auth/variants/ReviewPreviewNarrative.jsx
//
// For visitors who arrived from a drafted-replies preview page.
//
// They came for one specific thing — an unanswered review on their own Google
// profile — so this drops the three-feature pitch and the weather example, and
// continues the thread instead. Social posting gets one line at the end rather
// than equal billing.
//
// ctx.businessName and ctx.vertical may be null: anyone whose revfer_preview_ref
// was captured before those fields shipped has only a slug. Every use is
// guarded — the copy reads fine without them.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Link } from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  ArrowRightAlt as ArrowFlowIcon,
  SubdirectoryArrowRight as ArrowDownIcon,
  RateReview as ReviewIcon,
  EditNote as DraftIcon,
  TaskAlt as ApproveIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';

const GREEN = '#2E7D32';
const GREEN_BG = 'rgba(46,125,50,0.08)';
const GREEN_BORDER = 'rgba(46,125,50,0.2)';

// What happens on an ordinary week once it's running. Review-only on purpose.
const WEEKLY_FLOW = [
  {
    Icon: ReviewIcon,
    signal: 'A review lands',
    signalAt: '9:02pm',
    outcome: 'Draft written in your voice',
    outcomeAt: '9:06pm',
    mobileOutcome: 'Draft ready by 9:06pm',
  },
  {
    Icon: ApproveIcon,
    signal: 'You read it on your phone',
    signalAt: 'next morning',
    outcome: 'Tap approve, or edit it first',
    outcomeAt: '20 sec',
    mobileOutcome: 'Tap approve, or edit first',
  },
  {
    Icon: GoogleIcon,
    signal: 'Approved',
    signalAt: '',
    outcome: 'Posts to your Google profile',
    outcomeAt: 'Live',
    mobileOutcome: 'Posts to your Google profile',
  },
];

const SETUP_STEPS = [
  { Icon: DraftIcon, label: 'Create your account', detail: 'Email or Google, about a minute.' },
  { Icon: ReviewIcon, label: 'Add your business', detail: 'Name and address — we find your Google listing.' },
  { Icon: GoogleIcon, label: 'Connect Google', detail: 'So approved replies can post. This is the only step that needs permissions.' },
];

// ─── Pieces ───────────────────────────────────────────────────────────────────

const WeeklyFlowCard = ({ compact }) => (
  <Box sx={{
    p: compact ? 1.75 : 2.25,
    bgcolor: 'white',
    borderRadius: 2.5,
    border: '1px solid #E8EDF2',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  }}>
    <Typography
      variant="overline"
      sx={{
        color: '#90A4AE', letterSpacing: compact ? 1.8 : 2.5,
        fontSize: compact ? 10 : 11, fontWeight: 700,
        display: 'block', mb: compact ? 1.5 : 1.75,
        textAlign: compact ? 'center' : 'left', lineHeight: 1.4,
      }}
    >
      EVERY REVIEW, FROM NOW ON
    </Typography>

    {WEEKLY_FLOW.map(({ Icon, signal, signalAt, outcome, outcomeAt, mobileOutcome }) => (
      <Box
        key={signal}
        sx={{
          pb: 1.4, mb: 1.4,
          borderBottom: '1px dashed #E8EDF2',
          '&:last-of-type': { pb: 0, mb: 0, borderBottom: 'none' },
          ...(compact ? {} : { display: 'flex', alignItems: 'center', gap: 1.5 }),
        }}
      >
        {!compact && (
          <Box sx={{
            width: 34, height: 34, borderRadius: 1.5,
            bgcolor: GREEN_BG, border: `1px solid ${GREEN_BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon sx={{ color: GREEN, fontSize: 18 }} />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 0 }}>
            {compact && <Icon sx={{ color: GREEN, fontSize: 15, flexShrink: 0 }} />}
            <Typography variant="caption" sx={{ color: '#78909C', fontSize: compact ? 12 : 12.5, lineHeight: 1.4 }}>
              {signal}
              {signalAt && (
                <Box component="span" sx={{ color: '#B0BEC5' }}>
                  {compact ? `, ${signalAt}` : ` · ${signalAt}`}
                </Box>
              )}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: '#0D2B6B', fontWeight: 600,
              fontSize: compact ? 12.5 : 13.5,
              lineHeight: 1.45, mt: 0.4,
              pl: compact ? '23px' : 0,
              display: 'flex', alignItems: 'flex-start', gap: 0.6,
            }}
          >
            {compact
              ? <ArrowDownIcon sx={{ color: GREEN, fontSize: 14, mt: '2px', flexShrink: 0 }} />
              : <ArrowFlowIcon sx={{ color: GREEN, fontSize: 17, mt: '-1px', flexShrink: 0 }} />}
            {compact ? mobileOutcome : outcome}
          </Typography>
        </Box>

        {!compact && (
          <Box sx={{ flexShrink: 0, px: 1.25, py: 0.4, borderRadius: 5, bgcolor: GREEN_BG }}>
            <Typography variant="caption" sx={{ color: GREEN, fontWeight: 700, fontSize: 11 }}>
              {outcomeAt}
            </Typography>
          </Box>
        )}
      </Box>
    ))}

    <Box sx={{ mt: 1.4, pt: 1.2, borderTop: '1px dashed #E8EDF2' }}>
      <Typography
        variant="caption"
        sx={{
          color: '#455A7B', fontSize: compact ? 11.5 : 12,
          fontWeight: 500, lineHeight: 1.5, display: 'block',
          textAlign: compact ? 'center' : 'left',
        }}
      >
        You never have to remember to do it.
      </Typography>
    </Box>
  </Box>
);

const SetupCard = ({ compact }) => (
  <Box sx={{ p: compact ? 2 : 2.5, bgcolor: 'white', borderRadius: 2.5, border: '1px solid #E8EDF2', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
    <Typography variant="overline" sx={{ color: '#90A4AE', letterSpacing: 2.5, fontSize: 10, fontWeight: 700, display: 'block', mb: 2 }}>
      SETUP — ABOUT 15 MINUTES
    </Typography>

    {SETUP_STEPS.map(({ Icon, label, detail }, i) => (
      <Box
        key={label}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 2,
          mb: 2, pb: 2,
          borderBottom: '1px dashed #E8EDF2',
          '&:last-child': { mb: 0, pb: 0, borderBottom: 'none' },
        }}
      >
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          bgcolor: GREEN_BG, border: `1px solid ${GREEN_BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          position: 'relative',
        }}>
          <Icon sx={{ color: GREEN, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={700} color="#0D2B6B" sx={{ mb: 0.25 }}>
            {i + 1}. {label}
          </Typography>
          <Typography variant="caption" color="#546E7A" sx={{ lineHeight: 1.5, display: 'block' }}>
            {detail}
          </Typography>
        </Box>
      </Box>
    ))}

    <Box sx={{ pt: 1.5, mt: 0.5, borderTop: '1px dashed #E8EDF2' }}>
      <Typography variant="caption" sx={{ color: GREEN, fontWeight: 700, fontSize: 11, display: 'block', mb: 0.5 }}>
        Free tier available · $19/mo launch pricing
      </Typography>
      <Typography variant="caption" sx={{ color: '#90A4AE', fontWeight: 500, fontSize: 11 }}>
        No credit card to start · Regular $49/mo after launch
      </Typography>
    </Box>
  </Box>
);

const SecondaryLinks = ({ center = false }) => {
  const navigate = useNavigate();
  const linkSx = { color: '#1976D2', fontWeight: 600, fontSize: 13, cursor: 'pointer', border: 'none', background: 'none', '&:hover': { color: '#1565C0' } };
  return (
    <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid rgba(13,43,107,0.1)', ...(center ? { textAlign: 'center' } : {}) }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
        Want premium free, permanently?{' '}
        <Link component="button" onClick={() => navigate('/founders-circle')} underline="none" sx={linkSx}>
          Join the Founders Circle →
        </Link>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        Want the longer version?{' '}
        <Link component="button" onClick={() => navigate('/landing/business')} underline="none" sx={linkSx}>
          See how it works
          <ArrowIcon sx={{ fontSize: 15, verticalAlign: 'text-bottom', ml: 0.25 }} />
        </Link>
      </Typography>
    </Box>
  );
};

// ─── Variant ──────────────────────────────────────────────────────────────────

export default function ReviewPreviewNarrative({ compact = false, ctx = {} }) {
  const name = ctx.businessName;

  const eyebrow = name
    ? `The replies we drafted for ${name}`
    : 'The replies we drafted for your business';

  const headline = 'Those were a sample.';
  const headlineAccent = 'This is what happens every week.';

  const subhead = name
    ? `Revfer wrote those from ${name}'s own Google reviews. Once it's connected, it does that for every review that comes in — you read the draft and tap approve.`
    : `Revfer wrote those from your own Google reviews. Once it's connected, it does that for every review that comes in — you read the draft and tap approve.`;

  const chip = (
    <Chip
      label={eyebrow}
      size="small"
      sx={{
        mb: compact ? 1.5 : 3,
        bgcolor: GREEN_BG,
        color: GREEN,
        fontWeight: 700,
        fontSize: 11,
        border: `1px solid ${GREEN_BORDER}`,
        height: 'auto',
        py: 0.75,
        '& .MuiChip-label': { whiteSpace: 'normal', ...(compact ? { textAlign: 'center' } : {}) },
      }}
    />
  );

  const socialLine = (
    <Typography variant="caption" sx={{ color: '#546E7A', fontSize: 12, lineHeight: 1.6, display: 'block', mt: 2 }}>
      Revfer also turns your good reviews into Instagram and Facebook posts, and
      works out when to publish them. That comes with the same account.
    </Typography>
  );

  if (compact) {
    return (
      <Box sx={{ width: '100%', mb: 3, px: { xs: 1, sm: 0 } }}>
        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          {chip}
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: '#0D2B6B', lineHeight: 1.25, mb: 1.5, fontSize: { xs: '1.35rem', sm: '1.6rem' } }}>
            {headline}<br />
            <Box component="span" sx={{ color: '#1976D2' }}>{headlineAccent}</Box>
          </Typography>
          <Typography variant="body2" sx={{ color: '#455A7B', lineHeight: 1.65, maxWidth: 420, mx: 'auto', fontSize: { xs: 13, sm: 14 } }}>
            {subhead}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}><WeeklyFlowCard compact={true} /></Box>
        <Box sx={{ mb: 2 }}><SetupCard compact={true} /></Box>
        <Box sx={{ textAlign: 'center' }}>{socialLine}</Box>
        <SecondaryLinks center={true} />
      </Box>
    );
  }

  return (
    <Box sx={{ pr: 5, pt: 2 }}>
      {chip}

      <Typography variant="h2" component="h1" sx={{ fontWeight: 800, color: '#0D2B6B', lineHeight: 1.15, mb: 2.5, fontSize: { lg: '2.6rem' } }}>
        {headline}<br />
        <Box component="span" sx={{ color: '#1976D2' }}>{headlineAccent}</Box>
      </Typography>

      <Typography variant="body1" sx={{ color: '#455A7B', lineHeight: 1.75, fontSize: '1rem', mb: 3.5 }}>
        {subhead}
      </Typography>

      <Box sx={{ mb: 3 }}><WeeklyFlowCard compact={false} /></Box>
      <Box sx={{ mb: 3 }}><SetupCard compact={false} /></Box>

      {socialLine}
      <SecondaryLinks center={false} />
    </Box>
  );
}